import logger from '@/lib/logger'

import type { NextPageContext } from 'next'

/** fe-common's final decision for its header and footer variants. */
export type ChromeFlags = {
  isBrandRefresh: boolean
  isBlackFriday: boolean
  isBlackFridayPrereg: boolean
}

// fe-common's own call to client-tracking-context-svc gives up after 800ms.
const TIMEOUT_MS = 1000

const isChromeFlags = (body: unknown): body is ChromeFlags =>
  typeof body === 'object' &&
  body !== null &&
  typeof (body as ChromeFlags).isBrandRefresh === 'boolean' &&
  typeof (body as ChromeFlags).isBlackFriday === 'boolean' &&
  typeof (body as ChromeFlags).isBlackFridayPrereg === 'boolean'

/**
 * Asks fe-common which header and footer this visitor gets, during the server render. fe-common owns
 * the flags and how they read, so a change to them ships from fe-common alone. Passing all of them
 * makes them final: CommonHeader and CommonFooter skip their own lookup, which needs a CTC provider
 * this template does not mount.
 *
 * `undefined` on a client-side navigation (no request), without SERVER_FE_COMMON_URL, without a
 * `ctc-session` cookie, or on any failure. `_app.tsx` then uses this MFE's own assignment
 * (getOwnChromeFlags), so the components still get all three values and never try their own lookup,
 * which throws without a provider. The endpoint reads a missing or unknown session as legacy too, so it
 * is not called without one.
 */
export default async function getChromeFlags(ctx: NextPageContext): Promise<ChromeFlags | undefined> {
  const feCommonUrl = process.env.SERVER_FE_COMMON_URL

  if (!ctx.req || !feCommonUrl) {
    return undefined
  }

  try {
    // Imported here so the browser bundle never carries the tracking-context entry for it.
    const { getCtcIdFromCookie } = await import('@fe-toolkit/utility/client/tracking-context')
    const ctcSessionId = getCtcIdFromCookie(ctx.req.headers.cookie ?? '')

    if (!ctcSessionId) {
      return undefined
    }

    const response = await fetch(
      `${feCommonUrl.replace(/\/+$/, '')}/fe-common/api/chrome-flags?ctcSessionId=${encodeURIComponent(ctcSessionId)}`,
      { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(TIMEOUT_MS) },
    )

    if (!response.ok) {
      throw new Error(`fe-common returned ${response.status}`)
    }

    const body: unknown = await response.json()

    if (!isChromeFlags(body)) {
      throw new Error('fe-common returned an unexpected body')
    }

    return {
      isBrandRefresh: body.isBrandRefresh,
      isBlackFriday: body.isBlackFriday,
      isBlackFridayPrereg: body.isBlackFridayPrereg,
    }
  } catch (error) {
    logger.warn('fe-common chrome flags unavailable, header and footer render legacy', {
      error: error instanceof Error ? error.message : String(error),
    })

    return undefined
  }
}
